export const AI_USAGE_PERIODS = [
  'this-month',
  'last-month',
  'last-7-days',
  'last-30-days',
] as const;
export type AiUsagePeriod = (typeof AI_USAGE_PERIODS)[number];

export const DEFAULT_AI_USAGE_PERIOD: AiUsagePeriod = 'this-month';

export const AI_USAGE_PERIOD_LABELS: Record<AiUsagePeriod, string> = {
  'this-month': 'This month',
  'last-month': 'Last month',
  'last-7-days': 'Last 7 days',
  'last-30-days': 'Last 30 days',
};

const DAY_MS = 24 * 60 * 60 * 1000;

export type AiUsageRange = { from: string; to?: string };

// A period that runs to now leaves `to` to the API, so a refetch sees new turns under the same query key.
export function periodRange(period: AiUsagePeriod, now = new Date()): AiUsageRange {
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth();
  switch (period) {
    case 'last-month':
      return {
        from: new Date(Date.UTC(year, month - 1, 1)).toISOString(),
        to: new Date(Date.UTC(year, month, 1) - 1).toISOString(),
      };
    case 'last-7-days':
      return { from: new Date(now.getTime() - 7 * DAY_MS).toISOString() };
    case 'last-30-days':
      return { from: new Date(now.getTime() - 30 * DAY_MS).toISOString() };
    default:
      return { from: new Date(Date.UTC(year, month, 1)).toISOString() };
  }
}

export type DailyUsage = { date: string; create: number; edit: number; ask: number };

export function fillEmptyDays(
  daily: DailyUsage[],
  range: AiUsageRange,
  now = new Date(),
): DailyUsage[] {
  const byDate = new Map(daily.map(day => [day.date, day]));
  const dates = new Set(byDate.keys());
  const last = range.to ? Date.parse(range.to) : now.getTime();
  for (
    let time = Math.floor(Date.parse(range.from) / DAY_MS) * DAY_MS;
    time <= last;
    time += DAY_MS
  ) {
    dates.add(new Date(time).toISOString().slice(0, 10));
  }
  return [...dates].sort().map(date => byDate.get(date) ?? { date, create: 0, edit: 0, ask: 0 });
}

export function formatMicros(micros: number): string {
  if (micros > 0 && micros < 10_000) return '<$0.01';
  return `$${(micros / 1_000_000).toFixed(2)}`;
}
